import { useCallback, useState } from 'react';

import { getDocument } from '@atlaskit/browser-apis';

type UseCopyOptions = {
	/**
	 * Optional callback to fire analytics events when copy is triggered
	 */
	onCopy?: () => void;
	/**
	 * Duration in milliseconds before resetting the copied state
	 * @default 3000
	 */
	resetTimeout?: number;
};

type UseCopyReturn = {
	/**
	 * Boolean indicating if the text was recently copied
	 */
	copied: boolean;
	/**
	 * Function to copy the text to clipboard
	 */
	copy: () => Promise<void>;
};

/**
 * Hook to copy text to clipboard with fallback support and optional analytics
 * @param text - The text to copy to clipboard
 * @param options - Optional configuration for the hook
 * @returns Object containing the copy function and copied state
 */
export const useCopy = (text: string, options?: UseCopyOptions): UseCopyReturn => {
	const { onCopy, resetTimeout = 3000 } = options || {};
	const [copied, setCopied] = useState(false);

	const copy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			onCopy?.();

			// Reset the success message after the specified timeout
			setTimeout(() => {
				setCopied(false);
			}, resetTimeout);
		} catch (error) {
			// Fallback for browsers that don't support clipboard API
			const doc = getDocument();
			if (!doc) {
				return;
			}
			const textArea = doc.createElement('textarea');
			textArea.value = text;
			textArea.style.position = 'fixed';
			textArea.style.opacity = '0';
			doc.body.appendChild(textArea);
			textArea.select();
			doc.execCommand('copy');
			doc.body.removeChild(textArea);
			setCopied(true);
			onCopy?.();
			setTimeout(() => {
				setCopied(false);
			}, resetTimeout);
		}
	}, [text, onCopy, resetTimeout]);

	return { copy, copied };
};
